import { supabase }             from '../supabase.js';
import { openStudentDrill }     from './drills.js';
import { openEditStudentModal } from '../components/edit-student.js';
import { hasPart }              from '../lib/access.js';
import ExcelJS                  from 'exceljs';

const esc = s => String(s ?? '').replace(/[&<>"']/g, c =>
  ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));

// ── Excel palette & branding ──────────────────────────────────────────────────
const XC = {
  navy:   'FF1F3864', navyTxt: 'FF1F3864',
  blue:   'FF2E75B6', ltBlue:  'FFBDD7EE',
  altRow: 'FFDEEBF7', white:   'FFFFFFFF', black: 'FF000000',
  grnBg:  'FFE2EFDA', grnFg:   'FF375623',
  redBg:  'FFFFCCCC', redFg:   'FF9C0006',
  greyBg: 'FFE0E0E0', greyFg:  'FF505050',
};
const XL_ORG     = 'LondMarx Group (Pty) Ltd';
const XL_IMPL    = 'Implementing Partner: LondMarx Training (Pty) Ltd';
const XL_PROG    = 'UIF LAP SHORT SKILLS – 2025-26 IMPLEMENTATION';
const XL_CONTACT = 'Contact: Ntsikelelo Klaas  /  084 227 9921';

function xFill(argb)            { return { type:'pattern', pattern:'solid', fgColor:{argb} }; }
function xFont(sz,bold,argb,it) { return { name:'Calibri', size:sz||11, bold:!!bold, color:{argb:argb||XC.black}, italic:!!it }; }
function xAlign(h,wrap)         { return { horizontal:h||'left', vertical:'middle', wrapText:!!wrap }; }
function xBorder(w,argb)        { const s={style:w||'thin',color:{argb:argb||'FFB0BEC5'}}; return {top:s,left:s,bottom:s,right:s}; }

function xlNow() {
  return new Date().toLocaleDateString('en-ZA', { day:'2-digit', month:'long', year:'numeric' });
}

function xlBrand(ws, title, total, nc) {
  // Two dedicated white logo rows (logos overlaid by caller)
  [55, 38].forEach(h => {
    const r = ws.addRow(['']);
    ws.mergeCells(r.number, 1, r.number, nc);
    r.getCell(1).fill = xFill(XC.white);
    r.height = h;
  });
  // Text rows
  [
    { text: XL_ORG,  bg: XC.navy,   font: xFont(13,true,XC.white),   h: 20 },
    { text: XL_IMPL, bg: XC.blue,   font: xFont(10,false,XC.white),  h: 16 },
    { text: XL_PROG, bg: XC.blue,   font: xFont(10,true,XC.white),   h: 16 },
    { text: title,   bg: XC.ltBlue, font: xFont(13,true,XC.navyTxt), h: 24 },
    { text: `Report Date: ${xlNow()}   |   Total Enrolled Learners: ${total.toLocaleString()}   |   ${XL_CONTACT}`,
             bg: XC.white, font: xFont(9), h: 14 },
    { text: '', bg: XC.white, font: xFont(9), h: 6 },
  ].forEach(({ text, bg, font, h }) => {
    const row  = ws.addRow([text]);
    ws.mergeCells(row.number, 1, row.number, nc);
    const cell = row.getCell(1);
    cell.fill = xFill(bg); cell.font = font; cell.alignment = xAlign('center');
    row.height = h;
  });
}

function xlHdrRow(ws, values, bg, fgArgb, height) {
  const row = ws.addRow(values);
  row.height = height || 16;
  row.eachCell(cell => {
    cell.fill = xFill(bg); cell.font = xFont(10, true, fgArgb || XC.white);
    cell.alignment = xAlign('center'); cell.border = xBorder();
  });
  return row;
}

function xlSectionRow(ws, label, bg, fgArgb, nc, indent, height) {
  const row  = ws.addRow([' '.repeat(indent || 0) + label]);
  ws.mergeCells(row.number, 1, row.number, nc);
  const cell = row.getCell(1);
  cell.fill = xFill(bg); cell.font = xFont(10, true, fgArgb || XC.white);
  cell.alignment = xAlign('left'); row.height = height || 15;
  return row;
}

async function loadImgB64(url) {
  try {
    const r = await fetch(url);
    if (!r.ok) return null;
    let bin = '';
    new Uint8Array(await r.arrayBuffer()).forEach(b => (bin += String.fromCharCode(b)));
    return btoa(bin);
  } catch { return null; }
}

async function xlSave(wb, filename) {
  const buf  = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url  = URL.createObjectURL(blob);
  const a    = Object.assign(document.createElement('a'), { href:url, download:filename });
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

// ── Column definitions ────────────────────────────────────────────────────────
const ALL_COLS = [
  { key: 'no',          label: '#',                  xlWidth:  6, default: true  },
  { key: 'employee_no', label: 'Employee No.',        xlWidth: 16, default: true  },
  { key: 'first_name',  label: 'First Name',          xlWidth: 20, default: true  },
  { key: 'surname',     label: 'Surname',             xlWidth: 24, default: true  },
  { key: 'national_id', label: 'ID Number',           xlWidth: 16, default: true  },
  { key: 'tax_ref',     label: 'Tax Number',          xlWidth: 16, default: false },
  { key: 'programme',   label: 'Programme',           xlWidth: 28, default: false },
  { key: 'uif',         label: 'UIF Contributor',     xlWidth: 14, default: false },
  { key: 'location',    label: 'Location',            xlWidth: 24, default: false },
  { key: 'race',        label: 'Race',                xlWidth: 14, default: true  },
  { key: 'gender',      label: 'Gender',              xlWidth: 10, default: true  },
  { key: 'disability',  label: 'Disability (Y/N)',    xlWidth: 14, default: false },
  { key: 'status',      label: 'Status',              xlWidth: 13, default: true  },
  { key: 'start_date',  label: 'Start Date',          xlWidth: 14, default: false },
  { key: 'term_date',   label: 'Termination Date',    xlWidth: 18, default: false },
];

// ── Module state ──────────────────────────────────────────────────────────────
let _muniList           = [];
let _muniVenueIds       = new Map();
let _venueMap           = new Map();
let _classList          = [];
let _loaded             = false;
let _dropListenerAdded  = false;
const _state = {
  municipalities: new Set(),
  statuses:       new Set(),
  classes:        new Set(),
  visibleCols:    new Set(ALL_COLS.filter(c => c.default).map(c => c.key)),
  search:         '',
  startDateFrom:  '',
  startDateTo:    '',
};

const LR_STYLES = `<style>
  .lr-ms-wrap { display:inline-block; }
  .lr-ms-btn {
    display:inline-flex; align-items:center; justify-content:space-between; gap:8px;
    padding:8px 12px; font-size:13px; font-weight:500; min-width:170px;
    border:1px solid var(--border); border-radius:8px;
    background:var(--surface); color:var(--text); cursor:pointer; white-space:nowrap;
    font-family:inherit;
  }
  .lr-ms-btn:hover { background:var(--surface-2); }
  .lr-ms-btn.has-selection { border-color:#F97316; color:#C2550B; }
  .lr-ms-btn svg { flex-shrink:0; opacity:.5; }
  .lr-ms-drop {
    position:fixed; z-index:9999;
    background:var(--surface); border:1px solid var(--border);
    border-radius:10px; box-shadow:0 8px 32px rgba(0,0,0,.14);
    min-width:220px; max-height:300px; overflow-y:auto; padding:5px 0;
  }
  .lr-ms-item {
    display:flex; align-items:center; gap:9px;
    padding:7px 14px; font-size:13px; cursor:pointer; color:var(--text);
    user-select:none;
  }
  .lr-ms-item:hover { background:var(--surface-2); }
  .lr-ms-item input[type=checkbox] { accent-color:#F97316; width:14px; height:14px; flex-shrink:0; cursor:pointer; }
  .lr-ms-divider { border:none; border-top:1px solid var(--border); margin:4px 0; }
  .lr-col-btn { min-width:120px !important; }
</style>`;

window.addEventListener('learner:updated', () => { _loaded = false; });

// ── Entry point ───────────────────────────────────────────────────────────────
export async function renderLearners() {
  const el      = document.getElementById('view-learners');
  const isAdmin = hasPart('learners');

  el.innerHTML = `
    ${LR_STYLES}
    <div class="page-header" style="display:flex;align-items:flex-start;justify-content:space-between;flex-wrap:wrap;gap:12px">
      <div>
        <h1>Learner Masterlist</h1>
        <p>Enrolled learners grouped by municipality &rarr; site &rarr; class.</p>
      </div>
      <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap">
        ${isAdmin ? `<button id="lr-new-btn" class="btn btn-primary" style="white-space:nowrap">+ New Learner</button>` : ''}
        <button id="lr-export-btn" class="btn-secondary" disabled
          style="display:inline-flex;align-items:center;gap:7px;padding:8px 16px;font-size:13px;
                 font-weight:500;border:1px solid var(--border);border-radius:8px;
                 background:var(--surface);color:var(--text);cursor:pointer;white-space:nowrap">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
            stroke-linecap="round" stroke-linejoin="round" style="width:15px;height:15px">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          Export Excel
        </button>
      </div>
    </div>

    <div class="filters-row" style="flex-wrap:wrap;gap:8px">
      <div class="lr-ms-wrap">
        <button class="lr-ms-btn${_state.municipalities.size ? ' has-selection' : ''}" id="lr-muni-btn">
          ${_state.municipalities.size ? `${_state.municipalities.size} selected` : 'All Municipalities'}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="width:13px;height:13px"><path d="M6 9l6 6 6-6"/></svg>
        </button>
        <div class="lr-ms-drop" id="lr-muni-drop" style="display:none"></div>
      </div>
      <div class="lr-ms-wrap">
        <button class="lr-ms-btn${_state.statuses.size ? ' has-selection' : ''}" id="lr-status-btn">
          ${_state.statuses.size ? `${_state.statuses.size} selected` : 'All Statuses'}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="width:13px;height:13px"><path d="M6 9l6 6 6-6"/></svg>
        </button>
        <div class="lr-ms-drop" id="lr-status-drop" style="display:none"></div>
      </div>
      <div class="lr-ms-wrap">
        <button class="lr-ms-btn${_state.classes.size ? ' has-selection' : ''}" id="lr-class-btn">
          ${_state.classes.size ? `${_state.classes.size} selected` : 'All Classes'}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="width:13px;height:13px"><path d="M6 9l6 6 6-6"/></svg>
        </button>
        <div class="lr-ms-drop" id="lr-class-drop" style="display:none"></div>
      </div>
      <div class="lr-ms-wrap">
        <button class="lr-ms-btn lr-col-btn" id="lr-cols-btn">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
            stroke-linecap="round" stroke-linejoin="round" style="width:14px;height:14px;opacity:.6">
            <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/>
            <line x1="8" y1="18" x2="21" y2="18"/>
            <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/>
            <line x1="3" y1="18" x2="3.01" y2="18"/>
          </svg>
          Columns
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="width:13px;height:13px"><path d="M6 9l6 6 6-6"/></svg>
        </button>
        <div class="lr-ms-drop" id="lr-cols-drop" style="display:none;min-width:200px"></div>
      </div>
      <input type="text" id="lr-search" class="search-input"
        placeholder="Search name, ID or phone number…" value="${esc(_state.search)}" />
      <div style="display:inline-flex;align-items:center;gap:6px;font-size:13px;color:var(--text-muted)">
        <span>Start date:</span>
        <input type="date" id="lr-date-from" value="${esc(_state.startDateFrom)}"
          style="padding:7px 10px;font-size:13px;border:1px solid var(--border);border-radius:8px;
                 background:var(--surface);color:var(--text);font-family:inherit;cursor:pointer" />
        <span>to</span>
        <input type="date" id="lr-date-to" value="${esc(_state.startDateTo)}"
          style="padding:7px 10px;font-size:13px;border:1px solid var(--border);border-radius:8px;
                 background:var(--surface);color:var(--text);font-family:inherit;cursor:pointer" />
      </div>
    </div>

    <div id="lr-count" style="font-size:13px;color:var(--text-muted);margin-bottom:8px"></div>
    <div class="table-card" style="padding:0;overflow:hidden">
      <div id="lr-body" style="overflow-x:auto">
        <div style="padding:32px;text-align:center;color:var(--text-muted)">Loading&hellip;</div>
      </div>
    </div>`;

  // Wire search and new-learner button
  document.getElementById('lr-search').addEventListener('input',
    debounce(e => { _state.search = e.target.value.trim(); renderTable(); }, 300)
  );
  document.getElementById('lr-date-from').addEventListener('change',
    e => { _state.startDateFrom = e.target.value; renderTable(); }
  );
  document.getElementById('lr-date-to').addEventListener('change',
    e => { _state.startDateTo = e.target.value; renderTable(); }
  );
  if (isAdmin) {
    document.getElementById('lr-new-btn')?.addEventListener('click', () =>
      openEditStudentModal({ onSave: () => { _loaded = false; renderLearners(); } })
    );
  }

  if (!_loaded) await loadData();

  // Populate dropdowns
  buildMultiSelect(
    'lr-muni-btn', 'lr-muni-drop',
    _muniList.map(m => ({ value: m.id, label: m.name })),
    _state.municipalities, 'All Municipalities',
    () => renderTable()
  );
  buildMultiSelect(
    'lr-status-btn', 'lr-status-drop',
    [
      { value: 'active',      label: 'Active'      },
      { value: 'withdrawn',   label: 'Dropout'     },
      { value: 'transferred', label: 'Transferred' },
      { value: 'graduated',   label: 'Graduated'   },
      { value: 'suspended',   label: 'Suspended'   },
    ],
    _state.statuses, 'All Statuses',
    () => renderTable()
  );
  buildMultiSelect(
    'lr-class-btn', 'lr-class-drop',
    _classList.map(c => ({ value: c.id, label: c.name })),
    _state.classes, 'All Classes',
    () => renderTable()
  );
  buildColPicker('lr-cols-btn', 'lr-cols-drop');

  // Wire open/close for every dropdown button (onclick = single handler, never accumulates)
  [['lr-muni-btn','lr-muni-drop'],['lr-status-btn','lr-status-drop'],['lr-class-btn','lr-class-drop'],['lr-cols-btn','lr-cols-drop']]
    .forEach(([btnId, dropId]) => {
      const btn  = document.getElementById(btnId);
      const drop = document.getElementById(dropId);
      if (!btn || !drop) return;
      btn.onclick = e => {
        e.stopPropagation();
        const isOpen = drop.style.display === 'block';
        closeAllDropdowns();
        if (!isOpen) {
          const r = btn.getBoundingClientRect();
          drop.style.top  = `${r.bottom + 4}px`;
          drop.style.left = `${r.left}px`;
          drop.style.display = 'block';
        }
      };
      drop.onclick = e => e.stopPropagation();
    });

  // Add document close-listener exactly once per page load
  if (!_dropListenerAdded) {
    document.addEventListener('click', closeAllDropdowns);
    _dropListenerAdded = true;
  }

  const exportBtn = document.getElementById('lr-export-btn');
  exportBtn.disabled = false;
  exportBtn.addEventListener('click', () => downloadExcel().catch(console.error));

  renderTable();
}

// ── Load all data ─────────────────────────────────────────────────────────────
async function loadData() {
  const STU = `student_uuid, national_id, tax_ref_number, first_name, surname, gender, race, status,
    employee_number, uif_contributor, disability, start_date, termination_date, phone_number`;

  const [clsRes] = await Promise.all([
    supabase.from('classes').select(`
      class_id, class_name,
      training_venues(
        training_venue_id, training_venue_name,
        towns(municipalities(municipality_id, municipality_name))
      )
    `).eq('is_test', false).order('class_name'),
  ]);

  const PAGE = 1000;
  let from = 0, allEnr = [];
  while (true) {
    const { data, error } = await supabase
      .from('student_class_enrollments')
      .select(`class_id, students(${STU})`)
      .eq('status', 'enrolled')
      .range(from, from + PAGE - 1);
    if (error) break;
    allEnr = allEnr.concat(data || []);
    if (!data || data.length < PAGE) break;
    from += PAGE;
  }

  const stuByClass = new Map();
  for (const e of allEnr) {
    if (!e.students) continue;
    if (!stuByClass.has(e.class_id)) stuByClass.set(e.class_id, []);
    stuByClass.get(e.class_id).push(e.students);
  }

  _muniList = []; _muniVenueIds = new Map(); _venueMap = new Map();
  const muniSeen = new Map();

  for (const cls of clsRes.data || []) {
    const venue = cls.training_venues;
    const muni  = venue?.towns?.municipalities;
    if (!venue || !muni) continue;

    if (!muniSeen.has(muni.municipality_id)) {
      muniSeen.set(muni.municipality_id, muni.municipality_name);
      _muniVenueIds.set(muni.municipality_id, new Set());
    }
    _muniVenueIds.get(muni.municipality_id).add(venue.training_venue_id);

    if (!_venueMap.has(venue.training_venue_id)) {
      _venueMap.set(venue.training_venue_id, {
        id: venue.training_venue_id, name: venue.training_venue_name,
        muniId: muni.municipality_id, classes: [],
      });
    }
    _venueMap.get(venue.training_venue_id).classes.push({
      id:       cls.class_id,
      name:     cls.class_name,
      students: (stuByClass.get(cls.class_id) || []).sort((a, b) => {
        const af = (a.first_name || '').toLowerCase(), bf = (b.first_name || '').toLowerCase();
        if (af !== bf) return af < bf ? -1 : 1;
        const as = (a.surname || '').toLowerCase(), bs = (b.surname || '').toLowerCase();
        return as < bs ? -1 : as > bs ? 1 : 0;
      }),
    });
  }

  _muniList = [...muniSeen.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));

  _classList = [];
  for (const venue of _venueMap.values()) {
    for (const cls of venue.classes) _classList.push({ id: cls.id, name: cls.name });
  }
  _classList.sort((a, b) => a.name.localeCompare(b.name));

  _loaded = true;
}

// ── Grouped table render ──────────────────────────────────────────────────────
function renderTable() {
  const body    = document.getElementById('lr-body');
  const countEl = document.getElementById('lr-count');
  if (!body) return;

  const cols   = ALL_COLS.filter(c => _state.visibleCols.has(c.key));
  const nc     = cols.length;
  const q      = _state.search.toLowerCase();
  const mFilt  = _state.municipalities;
  const sFilt  = _state.statuses;
  const cFilt  = _state.classes;
  const sdFrom = _state.startDateFrom;
  const sdTo   = _state.startDateTo;
  const munis  = mFilt.size ? _muniList.filter(m => mFilt.has(m.id)) : _muniList;

  let total = 0;
  const rows = [];

  for (const muni of munis) {
    const vKey = v => v.classes.length === 1 ? v.classes[0].name : v.name;
    const venues = [...(_muniVenueIds.get(muni.id) || [])]
      .map(id => _venueMap.get(id)).filter(Boolean)
      .sort((a, b) => vKey(a).localeCompare(vKey(b)));

    const muniBody = [];

    for (const venue of venues) {
      const venueBody = [];
      const classes   = [...venue.classes].sort((a, b) => a.name.localeCompare(b.name));

      for (const cls of classes) {
        if (cFilt.size && !cFilt.has(cls.id)) continue;
        let stu = [...cls.students].sort((a, b) => {
          const af = (a.first_name || '').toLowerCase(), bf = (b.first_name || '').toLowerCase();
          if (af !== bf) return af < bf ? -1 : 1;
          const as2 = (a.surname || '').toLowerCase(), bs2 = (b.surname || '').toLowerCase();
          return as2 < bs2 ? -1 : as2 > bs2 ? 1 : 0;
        });
        if (sFilt.size) stu = stu.filter(s => sFilt.has(s.status));
        if (sdFrom) stu = stu.filter(s => !s.start_date || s.start_date >= sdFrom);
        if (sdTo)   stu = stu.filter(s => !s.start_date || s.start_date <= sdTo);
        if (q) stu = stu.filter(s =>
          s.surname?.toLowerCase().includes(q) ||
          s.first_name?.toLowerCase().includes(q) ||
          s.national_id?.includes(q) ||
          s.phone_number?.includes(q)
        );
        if (!stu.length) continue;

        venueBody.push(`
          <tr>
            <td colspan="${nc}" style="background:#FFF7ED;color:#C2550B;font-weight:600;font-size:12px;
                                       padding:5px 12px 5px 28px;border-top:1px solid #FED7AA">
              ${esc(cls.name)}
              <span style="font-weight:400;color:#EA6C0A;margin-left:6px">${stu.length} learner${stu.length !== 1 ? 's' : ''}</span>
            </td>
          </tr>`);

        stu.forEach((s, i) => {
          total++;
          const bg = i % 2 === 0 ? '' : 'background:var(--surface-2)';
          venueBody.push(`
            <tr class="clickable-row" data-student-uuid="${esc(s.student_uuid)}"
                data-student-name="${esc((s.first_name || '') + ' ' + (s.surname || ''))}">
              ${cols.map(c => stuCellHtml(c.key, s, total, muni.name, venue.name, cls.name, bg)).join('')}
            </tr>`);
        });
      }

      if (!venueBody.length) continue;
      muniBody.push(`
        <tr>
          <td colspan="${nc}" style="background:#F97316;color:#fff;font-weight:600;font-size:12px;
                                     padding:6px 12px 6px 16px;border-top:1px solid #EA6C0A">
            ${esc(venue.name)}
          </td>
        </tr>${venueBody.join('')}`);
    }

    if (!muniBody.length) continue;
    rows.push(`
      <tr>
        <td colspan="${nc}" style="background:#FDBA74;color:#7C2D12;font-weight:700;font-size:13px;
                                   padding:8px 12px;text-transform:uppercase;letter-spacing:.04em">
          ${esc(muni.name)}
        </td>
      </tr>${muniBody.join('')}`);
  }

  if (!rows.length) {
    body.innerHTML = `<div style="padding:32px;text-align:center;color:var(--text-muted);font-size:13px">No learners match the current filters.</div>`;
    if (countEl) countEl.textContent = '';
    return;
  }

  body.innerHTML = `
    <table class="data-table" style="border-collapse:collapse;width:100%">
      <thead><tr>${cols.map(c => `<th>${esc(c.label)}</th>`).join('')}</tr></thead>
      <tbody>${rows.join('')}</tbody>
    </table>`;

  body.querySelectorAll('.clickable-row').forEach(row =>
    row.addEventListener('click', () =>
      openStudentDrill({ studentUuid: row.dataset.studentUuid, studentName: row.dataset.studentName })
    )
  );

  if (countEl) countEl.textContent = `${total.toLocaleString()} learner${total !== 1 ? 's' : ''} shown`;
}

function stuCellHtml(key, s, n, muniName, venueName, className, rowBg) {
  const base = `style="${rowBg}"`;
  const mono = `style="font-family:monospace;font-size:12px;${rowBg ? rowBg + ';' : ''}"`;
  const sm   = `style="font-size:12px;${rowBg ? rowBg + ';' : ''}"`;
  const yn   = v => v === true ? 'Y' : v === false ? 'N' : '—';
  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-ZA') : '—';
  switch (key) {
    case 'no':          return `<td style="color:var(--text-muted);font-size:11px;text-align:center;${rowBg}">${n}</td>`;
    case 'employee_no': return `<td ${mono}>${esc(s.employee_number || '—')}</td>`;
    case 'first_name':  return `<td ${base}>${esc(s.first_name || '')}</td>`;
    case 'surname':     return `<td ${base}>${esc(s.surname || '')}</td>`;
    case 'national_id': return `<td ${mono}>${esc(s.national_id || '—')}</td>`;
    case 'tax_ref':     return `<td ${mono}>${esc(s.tax_ref_number || '—')}</td>`;
    case 'programme':   return `<td ${sm}>${esc(className)}</td>`;
    case 'uif':         return `<td ${base}>${yn(s.uif_contributor)}</td>`;
    case 'location':    return `<td ${sm}>${esc(muniName)}</td>`;
    case 'race':        return `<td ${base}>${esc(s.race || '—')}</td>`;
    case 'gender':      return `<td ${base}>${esc(s.gender || '—')}</td>`;
    case 'disability':  return `<td ${base}>${yn(s.disability)}</td>`;
    case 'status':      return `<td ${base}><span class="badge ${statusBadge(s.status)}">${statusLabel(s.status)}</span></td>`;
    case 'start_date':  return `<td ${sm}>${fmtDate(s.start_date)}</td>`;
    case 'term_date':   return `<td ${sm}>${fmtDate(s.termination_date)}</td>`;
    default:            return `<td ${base}>—</td>`;
  }
}

// ── Excel export ──────────────────────────────────────────────────────────────
async function downloadExcel() {
  const cols = ALL_COLS.filter(c => _state.visibleCols.has(c.key));
  const NC   = cols.length;

  // Apply the same filters as the on-screen table
  const q      = _state.search.toLowerCase();
  const mFilt  = _state.municipalities;
  const sFilt  = _state.statuses;
  const cFilt  = _state.classes;
  const sdFrom = _state.startDateFrom;
  const sdTo   = _state.startDateTo;
  const munis  = mFilt.size ? _muniList.filter(m => mFilt.has(m.id)) : _muniList;

  // Build filtered data structure once, reuse for writing + summary
  const filteredMunis = [];
  for (const muni of munis) {
    const vKey = v => v.classes.length === 1 ? v.classes[0].name : v.name;
    const venues = [...(_muniVenueIds.get(muni.id) || [])]
      .map(id => _venueMap.get(id)).filter(Boolean)
      .sort((a, b) => vKey(a).localeCompare(vKey(b)));
    const fVenues = [];
    for (const venue of venues) {
      const fClasses = [];
      for (const cls of [...venue.classes].sort((a, b) => a.name.localeCompare(b.name))) {
        if (cFilt.size && !cFilt.has(cls.id)) continue;
        let stu = cls.students;
        if (sFilt.size) stu = stu.filter(s => sFilt.has(s.status));
        if (sdFrom) stu = stu.filter(s => !s.start_date || s.start_date >= sdFrom);
        if (sdTo)   stu = stu.filter(s => !s.start_date || s.start_date <= sdTo);
        if (q) stu = stu.filter(s =>
          s.surname?.toLowerCase().includes(q) ||
          s.first_name?.toLowerCase().includes(q) ||
          s.national_id?.includes(q) ||
          s.phone_number?.includes(q)
        );
        if (stu.length) fClasses.push({ ...cls, students: stu });
      }
      if (fClasses.length) fVenues.push({ ...venue, classes: fClasses });
    }
    if (fVenues.length) filteredMunis.push({ muni, venues: fVenues });
  }

  const allStudents = filteredMunis.flatMap(m => m.venues.flatMap(v => v.classes.flatMap(c => c.students)));

  const [b64Left, b64Lm, b64Lap] = await Promise.all([
    loadImgB64('/logo-left.png'),
    loadImgB64('/londmarx%20logo.png'),
    loadImgB64('/logo-lap.png'),
  ]);

  const wb = new ExcelJS.Workbook();
  wb.creator = 'LondMarx Learnership Platform'; wb.created = new Date();

  const ws = wb.addWorksheet('Learner Masterlist');
  ws.columns = cols.map(c => ({ width: c.xlWidth }));

  xlBrand(ws, 'LEARNER MASTERLIST', allStudents.length, NC);

  const LZ = NC >= 6 ? 2 : NC >= 3 ? 1 : 0;
  if (LZ > 0) {
    const addLogoImg = (b64, tl, ext) => {
      if (!b64) return;
      ws.addImage(wb.addImage({ base64: b64, extension: 'png' }), { tl, ext });
    };
    // Row 0 = white logo row, h=55pt ≈ 73px
    // logo-left 343×101 → fit height 70px: width = 70×343/101 = 238 → cap at 220
    addLogoImg(b64Left, { row: 0, col: 0       }, { width: 220, height: 65 });
    // londmarx 360×239 → fit height 70px: width = 70×360/239 = 106 → use 100
    addLogoImg(b64Lm,   { row: 0, col: NC - LZ }, { width: 100, height: 66 });
    // Row 1 = second white logo row, h=38pt ≈ 51px
    // logo-lap 256×117 → fit height 48px: width = 48×256/117 = 105 → use 100
    addLogoImg(b64Lap,  { row: 1, col: NC - LZ }, { width: 100, height: 50 });
  }

  xlHdrRow(ws, cols.map(c => c.label), XC.navy, XC.white, 18);
  ws.views = [{ state: 'frozen', xSplit: 0, ySplit: ws.rowCount }];

  const yn      = v => v === true ? 'Y' : v === false ? 'N' : '';
  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-ZA') : '';

  function stuXlVals(colKey, s, n, muniName, venueName, className) {
    switch (colKey) {
      case 'no':          return n;
      case 'employee_no': return s.employee_number || '';
      case 'first_name':  return s.first_name || '';
      case 'surname':     return s.surname || '';
      case 'national_id': return s.national_id || '';
      case 'tax_ref':     return s.tax_ref_number || '';
      case 'programme':   return className;
      case 'uif':         return yn(s.uif_contributor);
      case 'location':    return muniName;
      case 'race':        return s.race || '';
      case 'gender':      return s.gender || '';
      case 'disability':  return yn(s.disability);
      case 'status':      return statusLabel(s.status);
      case 'start_date':  return fmtDate(s.start_date);
      case 'term_date':   return fmtDate(s.termination_date);
      default:            return '';
    }
  }

  const statusIdx = cols.findIndex(c => c.key === 'status');
  let n = 1;
  for (const { muni, venues } of filteredMunis) {
    xlSectionRow(ws, muni.name.toUpperCase(), XC.navy,   XC.white,   NC, 0, 16);
    for (const venue of venues) {
      xlSectionRow(ws, venue.name,            XC.blue,   XC.white,   NC, 1, 14);
      for (const cls of venue.classes) {
        xlSectionRow(ws, cls.name,            XC.ltBlue, XC.navyTxt, NC, 2, 13);
        cls.students.forEach((s, si) => {
          const bg  = si % 2 === 0 ? XC.white : XC.altRow;
          const row = ws.addRow(cols.map(c => stuXlVals(c.key, s, n, muni.name, venue.name, cls.name)));
          row.height = 13;
          row.eachCell((cell, cn) => {
            cell.fill = xFill(bg); cell.font = xFont(9); cell.border = xBorder();
            cell.alignment = cn === 1 ? xAlign('center') : xAlign('left');
          });
          if (statusIdx >= 0) {
            const sc = row.getCell(statusIdx + 1);
            if      (s.status === 'active')      { sc.fill = xFill(XC.grnBg);  sc.font = xFont(9, false, XC.grnFg);  }
            else if (s.status === 'withdrawn')   { sc.fill = xFill(XC.redBg);  sc.font = xFont(9, false, XC.redFg);  }
            else if (s.status === 'transferred') { sc.fill = xFill(XC.greyBg); sc.font = xFont(9, false, XC.greyFg); }
          }
          n++;
        });
      }
      ws.addRow([]).height = 4;
    }
  }

  // ── Summary (from filtered data) ──────────────────────────────────────────
  ws.addRow([]).height = 12;
  const summHdr = ws.addRow(['SUMMARY']);
  ws.mergeCells(summHdr.number, 1, summHdr.number, NC);
  Object.assign(summHdr.getCell(1), {
    fill: xFill(XC.navy), font: xFont(12, true, XC.white), alignment: xAlign('center'),
  });
  summHdr.height = 20;

  const sc = {};
  for (const s of allStudents) sc[s.status] = (sc[s.status] || 0) + 1;

  xlHdrRow(ws, ['', 'Category', 'Count', ...Array(NC - 3).fill('')], XC.blue, XC.white, 14);
  [
    ['Total',                  allStudents.length,   true],
    ['Active',                 sc['active']      || 0, false],
    ['Withdrawn / Dropout',    sc['withdrawn']   || 0, false],
    ['Transferred',            sc['transferred'] || 0, false],
    ['Graduated',              sc['graduated']   || 0, false],
    ['Suspended',              sc['suspended']   || 0, false],
  ].forEach(([label, count, bold], i) => {
    const r = ws.addRow(['', label, count, ...Array(NC - 3).fill('')]);
    r.height = 14;
    const bg = i % 2 === 0 ? XC.white : XC.altRow;
    [2, 3].forEach(cn => {
      const c = r.getCell(cn);
      c.fill = xFill(bg); c.font = xFont(10, bold); c.border = xBorder();
      c.alignment = cn === 2 ? xAlign('left') : xAlign('center');
    });
  });

  ws.addRow([]).height = 6;
  xlHdrRow(ws, ['', 'Municipality', 'Learners', ...Array(NC - 3).fill('')], XC.blue, XC.white, 14);
  filteredMunis.forEach(({ muni, venues }, i) => {
    const count = venues.flatMap(v => v.classes).reduce((a, c) => a + c.students.length, 0);
    const r  = ws.addRow(['', muni.name, count, ...Array(NC - 3).fill('')]);
    r.height = 13;
    const bg = i % 2 === 0 ? XC.white : XC.altRow;
    [2, 3].forEach(cn => {
      const c = r.getCell(cn);
      c.fill = xFill(bg); c.font = xFont(10); c.border = xBorder();
      c.alignment = cn === 2 ? xAlign('left') : xAlign('center');
    });
  });

  // ── Stats table ────────────────────────────────────────────────────────────
  ws.addRow([]).height = 12;

  const half = Math.max(2, Math.floor(NC / 2));
  const iCol = Math.min(half + 1, NC);
  const pCol = NC >= half + 3 ? half + 2 : Math.min(half + 2, NC);
  const aCol = NC;

  const TN = { style:'thin', color:{argb:'FFB0B0B0'} };
  function stc(cell, val, bold, bg) {
    cell.value     = val ?? '';
    cell.font      = xFont(10, !!bold);
    cell.fill      = xFill(bg || XC.white);
    cell.border    = { top:TN, bottom:TN, left:TN, right:TN };
    cell.alignment = xAlign('left');
  }

  // Header row
  {
    const r = ws.addRow(Array(NC).fill(''));
    r.height = 15;
    if (half > 1) ws.mergeCells(r.number, 1, r.number, half);
    stc(r.getCell(1), '', false, XC.greyBg);
    stc(r.getCell(iCol), 'Item', true, XC.greyBg);
    r.getCell(iCol).alignment = xAlign('center');
    if (pCol !== iCol) { stc(r.getCell(pCol), 'Percentage', true, XC.greyBg); r.getCell(pCol).alignment = xAlign('center'); }
    if (aCol !== pCol && aCol !== iCol) { stc(r.getCell(aCol), 'Actual Number', true, XC.greyBg); r.getCell(aCol).alignment = xAlign('center'); }
  }

  // Data rows
  [
    ['UIF Beneficiaries',     'UIF Contributors'    ],
    ['Non-UIF Beneficiaries', 'UIF Non-Contributors'],
    ['Vulnerable Group',      'Youth'               ],
    ['Non-Vulnerable Group',  'Women'               ],
    ['',                      'PWD'                 ],
  ].forEach(([left, item]) => {
    const r = ws.addRow(Array(NC).fill(''));
    r.height = 15;
    if (half > 1) ws.mergeCells(r.number, 1, r.number, half);
    stc(r.getCell(1), left, !!left);
    stc(r.getCell(iCol), item, false);
    if (pCol !== iCol)                  stc(r.getCell(pCol), '', false);
    if (aCol !== pCol && aCol !== iCol) stc(r.getCell(aCol), '', false);
  });

  // ── Signature blocks ───────────────────────────────────────────────────────
  const sc2 = Math.min(2, NC);
  const sc3 = NC >= 4 ? Math.min(Math.max(3, Math.round(NC * 0.35)), NC) : Math.min(3, NC);
  const sc4 = NC >= 5 ? Math.min(Math.max(4, Math.round(NC * 0.60)), NC) : Math.min(4, NC);

  const sigBlocks = [
    { col2:'',                 col3:'Partner Representative', col4:'LondMarx CEO',              dateLabel:'Date'  },
    { col2:'Nozi Tetyana',     col3:'DD:LAP',                 col4:'N. Tetyana',                dateLabel:'Date:' },
    { col2:'Maeleletse Nape',  col3:'Director (EC, LIMP & NW)', col4:'',                        dateLabel:'Date'  },
  ];

  sigBlocks.forEach(({ col2, col3, col4, dateLabel }, bi) => {
    ws.addRow([]).height = bi === 0 ? 14 : 10;

    // NAME row
    const nv = Array(NC).fill('');
    nv[0] = 'NAME';
    if (col2 && sc2 <= NC)                              nv[sc2 - 1] = col2;
    if (col3 && sc3 <= NC && sc3 !== sc2)               nv[sc3 - 1] = col3;
    if (col4 && sc4 <= NC && sc4 !== sc3 && sc4 !== sc2) nv[sc4 - 1] = col4;
    nv[NC - 1] = dateLabel;
    const nameRow = ws.addRow(nv);
    nameRow.height = 16;
    for (let c = 1; c <= NC; c++) stc(nameRow.getCell(c), nv[c-1], c === 1 || c === NC);

    // SIGNATURE row
    const sigRow = ws.addRow(Array(NC).fill(''));
    sigRow.height = 40;
    for (let c = 1; c <= NC; c++) stc(sigRow.getCell(c), c === 1 ? 'SIGNATURE' : '', c === 1);

    // DATE row
    const dateRow = ws.addRow(Array(NC).fill(''));
    dateRow.height = 16;
    for (let c = 1; c <= NC; c++) stc(dateRow.getCell(c), c === 1 ? 'DATE' : '', c === 1);
  });

  await xlSave(wb, `Learner_Masterlist_${new Date().toISOString().slice(0,10)}.xlsx`);
}

// ── Multi-select dropdown ─────────────────────────────────────────────────────
function buildMultiSelect(btnId, dropId, items, stateSet, placeholder, onChange) {
  const btn  = document.getElementById(btnId);
  const drop = document.getElementById(dropId);
  if (!btn || !drop) return;

  drop.innerHTML = `
    <label class="lr-ms-item">
      <input type="checkbox" id="${btnId}-all" ${stateSet.size === 0 ? 'checked' : ''}>
      <span style="font-weight:600">All</span>
    </label>
    <hr class="lr-ms-divider">
    ${items.map(it => `
      <label class="lr-ms-item">
        <input type="checkbox" value="${esc(String(it.value))}" ${stateSet.has(it.value) ? 'checked' : ''}>
        <span>${esc(it.label)}</span>
      </label>`).join('')}`;

  const allCb   = drop.querySelector(`#${btnId}-all`);
  const itemCbs = () => [...drop.querySelectorAll('input[value]')];

  function updateBtn() {
    const n = stateSet.size;
    btn.innerHTML = `${n === 0 ? placeholder : `${n} selected`}
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
        style="width:13px;height:13px;flex-shrink:0;opacity:.5"><path d="M6 9l6 6 6-6"/></svg>`;
    btn.classList.toggle('has-selection', n > 0);
    if (allCb) allCb.checked = n === 0;
  }

  allCb?.addEventListener('change', () => {
    stateSet.clear();
    itemCbs().forEach(cb => { cb.checked = false; });
    updateBtn(); onChange();
  });

  itemCbs().forEach(cb => {
    cb.addEventListener('change', () => {
      if (cb.checked) stateSet.add(cb.value); else stateSet.delete(cb.value);
      if (allCb) allCb.checked = stateSet.size === 0;
      updateBtn(); onChange();
    });
  });

  updateBtn();
}

// ── Column picker ─────────────────────────────────────────────────────────────
function buildColPicker(btnId, dropId) {
  const btn  = document.getElementById(btnId);
  const drop = document.getElementById(dropId);
  if (!btn || !drop) return;

  drop.innerHTML = ALL_COLS.map(c => `
    <label class="lr-ms-item">
      <input type="checkbox" value="${esc(c.key)}" ${_state.visibleCols.has(c.key) ? 'checked' : ''}>
      <span>${esc(c.label)}</span>
    </label>`).join('');

  drop.querySelectorAll('input[type=checkbox]').forEach(cb => {
    cb.addEventListener('change', () => {
      if (cb.checked) _state.visibleCols.add(cb.value);
      else if (_state.visibleCols.size > 1) _state.visibleCols.delete(cb.value);
      else cb.checked = true;
      renderTable();
    });
  });

}

// ── Helpers ───────────────────────────────────────────────────────────────────
function closeAllDropdowns() {
  document.querySelectorAll('.lr-ms-drop').forEach(d => { d.style.display = 'none'; });
}

function statusBadge(s) {
  return s === 'active' ? 'badge-green' : s === 'withdrawn' ? 'badge-red' :
    s === 'graduated' || s === 'completed' ? 'badge-blue' : 'badge-gray';
}
function statusLabel(s) {
  if (s === 'withdrawn') return 'Dropout';
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '—';
}
function debounce(fn, ms) { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; }
